import { createContext, useContext, useMemo, useReducer } from "react";

import type { Dispatch, ReactNode } from "react";

interface CarrierQuoteFiltersProviderProps {
  children: ReactNode;
}

interface CarrierQuoteFiltersContextState {
  filters: QuoteFilters;
}

interface SetQuoteFiltersAction {
  type: "setQuoteFilters";
  filters: QuoteFilters;
}

interface ClearQuoteFiltersAction {
  type: "clearQuoteFilters";
}

interface SetShipmentNameAction {
  type: "setShipmentName";
  shipmentName: string;
}

interface SetShipmentTypesAction {
  type: "setShipmentTypes";
  shipmentTypes: QuoteShipmentTypeFilterValue[];
}

interface SetQuoteStatusesAction {
  type: "setQuoteStatuses";
  statuses: string[];
}

interface SetOriginProvincesAction {
  type: "setOriginProvinces";
  provinces: string[];
}

interface SetDestinationProvincesAction {
  type: "setDestinationProvinces";
  provinces: string[];
}

interface SetPickupDateAfterAction {
  type: "setPickupDateAfter";
  date: string;
}

interface SetPickupDateBeforeAction {
  type: "setPickupDateBefore";
  date: string;
}

interface SetDeliveryDateAfterAction {
  type: "setDeliveryDateAfter";
  date: string;
}

interface SetDeliveryDateBeforeAction {
  type: "setDeliveryDateBefore";
  date: string;
}

interface SetShipmentStatusesAction {
  type: "setShipmentStatuses";
  statuses: string[];
}

interface SetEquipmentTypesAction {
  type: "setEquipmentTypes";
  equipmentTypes: string[];
}

interface SetServiceTypesAction {
  type: "setServiceTypes";
  serviceTypes: string[];
}

interface SetSortByAction {
  type: "setSortBy";
  sortBy: string;
}

type CarrierQuoteFiltersAction =
  | SetQuoteFiltersAction
  | ClearQuoteFiltersAction
  | SetShipmentNameAction
  | SetShipmentTypesAction
  | SetQuoteStatusesAction
  | SetOriginProvincesAction
  | SetDestinationProvincesAction
  | SetPickupDateAfterAction
  | SetPickupDateBeforeAction
  | SetDeliveryDateAfterAction
  | SetDeliveryDateBeforeAction
  | SetShipmentStatusesAction
  | SetEquipmentTypesAction
  | SetServiceTypesAction
  | SetSortByAction;

interface CarrierQuoteFiltersContextDispatch {
  dispatch: Dispatch<CarrierQuoteFiltersAction>;
}

const initialState: CarrierQuoteFiltersContextState = {
  filters: {},
};

const carrierQuoteFiltersContext = createContext<
  CarrierQuoteFiltersContextState & CarrierQuoteFiltersContextDispatch
>({
  ...initialState,
  dispatch: () => null,
});

function reducer(
  state: CarrierQuoteFiltersContextState,
  action: CarrierQuoteFiltersAction,
): CarrierQuoteFiltersContextState {
  switch (action.type) {
    case "setQuoteFilters":
      return { ...state, filters: action.filters };
    case "clearQuoteFilters":
      return { ...state, filters: {} };
    case "setShipmentName":
      return {
        ...state,
        filters: { ...state.filters, shipmentName: action.shipmentName },
      };
    case "setShipmentTypes":
      return {
        ...state,
        filters: { ...state.filters, shipmentTypes: action.shipmentTypes },
      };
    case "setQuoteStatuses":
      return {
        ...state,
        filters: { ...state.filters, quoteStatus: action.statuses },
      };
    case "setOriginProvinces":
      return {
        ...state,
        filters: { ...state.filters, originProvinces: action.provinces },
      };
    case "setDestinationProvinces":
      return {
        ...state,
        filters: { ...state.filters, destinationProvinces: action.provinces },
      };
    case "setPickupDateAfter":
      return {
        ...state,
        filters: { ...state.filters, pickupDateAfter: action.date },
      };
    case "setPickupDateBefore":
      return {
        ...state,
        filters: { ...state.filters, pickupDateBefore: action.date },
      };
    case "setDeliveryDateAfter":
      return {
        ...state,
        filters: { ...state.filters, deliveryDateAfter: action.date },
      };
    case "setDeliveryDateBefore":
      return {
        ...state,
        filters: { ...state.filters, deliveryDateBefore: action.date },
      };
    case "setShipmentStatuses":
      return {
        ...state,
        filters: { ...state.filters, shipmentStatus: action.statuses },
      };
    case "setEquipmentTypes":
      return {
        ...state,
        filters: { ...state.filters, equipmentTypes: action.equipmentTypes },
      };
    case "setServiceTypes":
      return {
        ...state,
        filters: { ...state.filters, serviceTypes: action.serviceTypes },
      };
    case "setSortBy":
      return {
        ...state,
        filters: { ...state.filters, sortBy: action.sortBy },
      };
    default:
      return state;
  }
}

export function CarrierQuoteFiltersProvider(
  props: CarrierQuoteFiltersProviderProps,
) {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  const memoState = useMemo(() => ({ ...state, dispatch }), [state]);

  return (
    <carrierQuoteFiltersContext.Provider value={memoState}>
      {children}
    </carrierQuoteFiltersContext.Provider>
  );
}

export function useCarrierQuoteFiltersContext() {
  return useContext(carrierQuoteFiltersContext);
}
